import React from 'react';
import { SOC2, RFP } from '../data/mock';
import PageHero from '../components/PageHero';
import { ShieldCheck, CheckCircle2, ArrowRight, Lock, Layers, FileCheck2, Database, Users, Cloud } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const ICONS = [ShieldCheck, Lock, Database, Users, Layers, Cloud];

export default function SOC2Page() {
  return (
    <div>
      <PageHero eyebrow={SOC2.hero.eyebrow} title={SOC2.hero.title} subtitle={SOC2.hero.subtitle} />

      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <span className="text-[12.5px] uppercase tracking-[0.22em] font-semibold text-[#E85A18]">What we cover</span>
            <h2 className="mt-3 text-[32px] lg:text-[42px] leading-[1.1] tracking-tight text-[#0F1B2D] font-semibold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Full coverage of enterprise healthcare IT / security questionnaires.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SOC2.offerings.map((o, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <div key={o.title} className="group rounded-2xl border border-slate-200/70 bg-white p-6 hover:border-orange-200 hover:bg-[#FFFBF6] hover:-translate-y-1 hover:shadow-[0_20px_50px_-25px_rgba(232,90,24,0.35)] transition-all duration-300">
                  <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#FFECD9] to-[#FFD5B4] flex items-center justify-center group-hover:from-[#FFB27A] group-hover:to-[#E85A18] transition-colors duration-300">
                    <Icon className="h-5 w-5 text-[#E85A18] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="mt-5 text-[17.5px] font-semibold text-[#0F1B2D]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {o.title}
                  </h3>
                  <p className="mt-2 text-[14px] leading-[1.65] text-slate-600">{o.copy}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <ProcessBlock title="How we work with you" subtitle="A tight, evidence-first workflow that gets InfoSec approvals moving." steps={SOC2.process} dark />

      <section className="py-20 lg:py-24 bg-[#FDF8F2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-[12.5px] uppercase tracking-[0.22em] font-semibold text-[#E85A18]">Outcomes</span>
            <h2 className="mt-3 text-[32px] lg:text-[40px] leading-[1.1] tracking-tight text-[#0F1B2D] font-semibold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              What changes for your team.
            </h2>
            <ul className="mt-8 space-y-4">
              {SOC2.outcomes.map((o) => (
                <li key={o} className="flex items-start gap-3 text-[15.5px] text-slate-700">
                  <CheckCircle2 className="h-5 w-5 text-[#FF7A2E] mt-0.5 flex-shrink-0" />
                  <span>{o}</span>
                </li>
              ))}
            </ul>
            <Button asChild className="mt-10 rounded-full bg-[#0F1B2D] hover:bg-[#1a2942] text-white h-12 px-6">
              <Link to="/contact">Talk to our SOC2 team <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
          <div className="relative rounded-3xl overflow-hidden border border-orange-100 shadow-[0_30px_80px_-30px_rgba(232,90,24,0.35)] bg-white p-8">
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[radial-gradient(closest-side,#FFD5B4,transparent_70%)]" />
            <div className="relative">
              <div className="text-[12px] uppercase tracking-widest text-slate-500 font-semibold">Sample coverage stack</div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                {['SOC2 Type I / II', 'HIPAA / DPDPA', 'HITRUST CSF', 'ISO 27001', 'SIG / CAIQ', 'VSA / Custom', 'DICOM Security', 'AI Model Governance'].map((t) => (
                  <div key={t} className="rounded-xl border border-orange-100 bg-[#FFF9F1] px-4 py-3 text-[13.5px] font-medium text-[#0F1B2D]">
                    <FileCheck2 className="inline h-4 w-4 text-[#E85A18] mr-2" />
                    {t}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// Reusable process block also used by RFP page
export function ProcessBlock({ title, subtitle, steps, dark }) {
  const bg = dark ? 'bg-[#0F1B2D]' : 'bg-white';
  const heading = dark ? 'text-white' : 'text-[#0F1B2D]';
  const sub = dark ? 'text-slate-300' : 'text-slate-600';
  return (
    <section className={`py-20 lg:py-24 ${bg} relative overflow-hidden`}>
      {dark && (
        <div className="pointer-events-none absolute inset-0 opacity-30">
          <div className="absolute -top-40 left-1/3 w-[600px] h-[600px] rounded-full bg-[radial-gradient(closest-side,#FF7A2E,transparent_70%)] opacity-25" />
        </div>
      )}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div className="max-w-2xl">
          <span className={`text-[12.5px] uppercase tracking-[0.22em] font-semibold ${dark ? 'text-[#FFB27A]' : 'text-[#E85A18]'}`}>Our Process</span>
          <h2 className={`mt-3 text-[32px] lg:text-[42px] leading-[1.1] tracking-tight ${heading} font-semibold`} style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {title}
          </h2>
          {subtitle && <p className={`mt-4 text-[15.5px] ${sub}`}>{subtitle}</p>}
        </div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((s) => (
            <div key={s.step} className={`rounded-2xl border p-7 transition-all duration-300 hover:-translate-y-1 ${dark ? 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-[#FF7A2E]/50' : 'bg-white border-orange-100 hover:border-[#FF7A2E]/50'}`}>
              <div className={`text-[13px] font-mono tracking-widest ${dark ? 'text-[#FFB27A]' : 'text-[#E85A18]'}`}>{s.step}</div>
              <h3 className={`mt-5 text-[19px] font-semibold ${heading} leading-tight`} style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {s.title}
              </h3>
              <p className={`mt-2 text-[14px] leading-[1.65] ${sub}`}>{s.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
